import React, { useCallback, useEffect, useRef, useState } from 'react'
import { formatRequestsListTableCheckDuplicates } from '../../../../helpers/tableUtils'
import {
    sendEventToIntercom,
    tagHotjarRecording,
} from '../../../../helpers/util'
import useAlert from '../../../../hooks/useAlert'
import useAuth from '../../../../hooks/useAuth'
import useFilterModal from '../../../../hooks/useFilterModal'
import useFleet from '../../../../hooks/useFleet'
import useGlobalLoading from '../../../../hooks/useGlobalLoading'
import useRequest from '../../../../hooks/useRequest'
import PropTypes from 'prop-types'
import { parse } from 'qs'
import { createServiceRepairSteps } from '../../../../helpers/createRequestutil'
import { useNetworkStatus } from '../../../../utils/networkStatus'
import { getFleetById } from '../../../../../../indexedDb/fleets'
import { getRequestsByType } from '../../../../../../indexedDb/requests'
import { processImagesHelper } from '../../../../utils/processImagesHelper'
import { checkDuplicates, handleAssetUpdate } from './utils/assetUtils'
import { createRequestHandler } from './utils/requestHandler'
import { checkAfterHours } from './utils/onCheckAfterHours'
import { handleNextStep } from './utils/updateStepState'
import RequestPageComponent from './components/RequestPageComponent'
import { onGettedAssetLocationInfo } from '../../../../utils/onGettedAssetLocationInfo'

const CreateRepairRequestFlow = ({ history }) => {
    const equipmentId = parse(window.location.search, {
        ignoreQueryPrefix: true,
    })?.equipmentId
    const isEstimate = parse(window.location.search, {
        ignoreQueryPrefix: true,
    })?.isEstimate
        ? true
        : false

    const [steps, setSteps] = useState(() => {
        const savedState = localStorage.getItem('repairRequestState')
        if (savedState) {
            const { steps: savedSteps } = JSON.parse(savedState)
            return savedSteps || createServiceRepairSteps(isEstimate)
        } else {
            return createServiceRepairSteps(isEstimate)
        }
    })

    const [formValues, setFormValues] = useState({})
    const [selectedAsset, setSelectedAsset] = useState([])
    const [error, setError] = useState(null)
    const [listOfRequests, setListOfRequests] = useState([])
    const [isValidForm, setIsValidForm] = useState(false)
    const [successfulSubmit, setSuccessfulSubmit] = useState(false)
    const [resetTouch, setResetTouch] = useState(false)
    const [afterHoursModalOpen, setAfterHoursModalOpen] = useState(false)
    const [skipDuplicate, setSkipDuplicate] = useState(false)
    const [assetInfo, setAssetInfo] = useState({})
    const [images, setImages] = useState([])
    const [photosIds, setPhotosIds] = useState([])
    const [imageRetryAttempted, setImageRetryAttempted] = useState(false)
    const [lastAfterHoursApproval, setLastAfterHoursApproval] = useState(null)
    const [hasUnsyncedData, setHasUnsyncedData] = useState(true)
    const [isLoadingAsset, setIsLoadingAsset] = useState(false)
    const equipmentFetchInitializedRef = useRef(null)

    const { user } = useAuth()
    const { isOffline } = useNetworkStatus()
    const { handleGetAsset } = useFleet()
    const { changeLoading } = useGlobalLoading()
    const { handleChangeAlert, handleChangeOpen } = useAlert()
    const formRef = useRef()

    const customAssetID = selectedAsset && selectedAsset[0]?.customId

    const getStoredAssetId = storedAsset =>
        storedAsset?.salesforceID || storedAsset?.customId || null

    const getStoredActiveStepName = storedSteps =>
        storedSteps?.find(step => step?.active)?.name

    const saveStateToStorage = (steps, assetInfo) => {
        assetInfo.customId = customAssetID
        const currentState = { steps, assetInfo }
        localStorage.setItem('repairRequestState', JSON.stringify(currentState))
    }

    const loadStateFromStorage = () => {
        const savedState = localStorage.getItem('repairRequestState')
        if (savedState) {
            const parsedState = JSON.parse(savedState)
            const parsedSteps = parsedState?.steps || []
            const parsedAssetInfo = parsedState?.assetInfo || {}
            const parsedAsset = Array.isArray(parsedAssetInfo)
                ? parsedAssetInfo[0]
                : parsedAssetInfo
            const activeStepName = getStoredActiveStepName(parsedSteps)
            const hasValidStoredAsset = Boolean(getStoredAssetId(parsedAsset))

            setSteps(parsedSteps)

            // On reload while selecting an asset, always force "no selection"
            // and avoid restoring stale/incomplete asset records.
            if (activeStepName === 'Select Asset' || !hasValidStoredAsset) {
                setAssetInfo({})
                setSelectedAsset([])
                return
            }

            setAssetInfo(parsedAsset)
            setSelectedAsset([parsedAsset])
        }
    }

    const handleSyncState = async () => {
        try {
            // When equipmentId is present, asset bootstrap is handled by the
            // dedicated equipment effect below. Avoid issuing a second request.
            if (equipmentId) {
                setHasUnsyncedData(false)
                return
            }

            const savedState = localStorage.getItem('repairRequestState')
            const parsedState = savedState ? JSON.parse(savedState) : {}
            const saveAssetInfo = parsedState?.assetInfo
            const parsedAsset = Array.isArray(saveAssetInfo)
                ? saveAssetInfo[0]
                : saveAssetInfo
            const activeStepName = getStoredActiveStepName(parsedState?.steps)
            const storedAssetId = getStoredAssetId(parsedAsset)

            if (activeStepName === 'Select Asset' || !storedAssetId) {
                setHasUnsyncedData(false)
                return
            }

            if (parsedAsset) {
                changeLoading(true)
                const paramsToGetFleetAsset = {
                    assetId: storedAssetId,
                    companyId: user?.company?.id,
                    getRequests: true,
                }

                handleGetAsset(
                    paramsToGetFleetAsset,
                    response => {
                        const locationInfo = onGettedAssetLocationInfo(response)
                        const updatedAssetInfo = {
                            ...parsedAsset,
                            serviceLocationId:
                                parsedAsset?.serviceLocation?.external_id ||
                                locationInfo.serviceLocationId,
                            model: response?.data?.asset?.model,
                            isCreatedOffline: isOffline,
                        }
                        setSteps(parsedState.steps || [])
                        setAssetInfo(updatedAssetInfo)
                        changeLoading(false)
                    },
                    onGettedAssetError,
                )
            }
            setHasUnsyncedData(false)
        } catch (error) {
            console.error('Error syncing state:', error)
        }
    }

    useEffect(() => {
        loadStateFromStorage()
    }, [isOffline])

    useEffect(() => {
        if (isOffline) {
            saveStateToStorage(steps, assetInfo)
            setHasUnsyncedData(true)
        }
    }, [isOffline, steps, assetInfo])

    useEffect(() => {
        if (!isOffline && hasUnsyncedData) {
            handleSyncState()
        }
    }, [isOffline, hasUnsyncedData])

    useEffect(() => {
        saveStateToStorage(steps, assetInfo)
    }, [steps, assetInfo])

    const {
        saveFilters,
        handleCreateServiceRequest,
        changeNeedsUpdate,
    } = useRequest()

    const {
        filteredData: filteredOpenRequests,
        onFilter: onFilterOpenRequests,
        setFilteredData: setFilterOpenRequests,
    } = useFilterModal(
        formatRequestsListTableCheckDuplicates,
        listOfRequests,
        saveFilters,
        selectedAsset[0],
    )

    useEffect(() => {
        setFilterOpenRequests(
            formatRequestsListTableCheckDuplicates(
                listOfRequests || [],
                selectedAsset[0],
            ),
        )
    }, [listOfRequests, selectedAsset, setFilterOpenRequests])

    // Add a reference to track the last processed asset ID
    const lastProcessedAssetIdRef = useRef(null)

    // Add effect to reset listOfRequests when selected asset changes
    useEffect(() => {
        if (selectedAsset && selectedAsset[0]) {
            const currentAssetId =
                selectedAsset[0].salesforceID || selectedAsset[0].customId

            // Only reset if we're selecting a different asset than before
            if (lastProcessedAssetIdRef.current !== currentAssetId) {
                setListOfRequests([])
                setSkipDuplicate(false)
                lastProcessedAssetIdRef.current = currentAssetId
            }
        }
    }, [selectedAsset])

    useEffect(() => {
        const activeStep = steps?.find(f => f.active === true)

        // Only proceed if we're not currently loading asset data
        if (!isLoadingAsset && activeStep?.name === 'Check Duplicates') {
            // Get current asset ID to ensure we're working with fresh data
            const currentAssetId =
                selectedAsset[0]?.salesforceID || selectedAsset[0]?.customId

            // Make sure this matches our last processed asset
            if (lastProcessedAssetIdRef.current === currentAssetId) {
                // Skip the duplicates step if:
                // 1. We're on the Check Duplicates step AND skipDuplicate is true
                // 2. OR we have an equipmentId AND there are no linked requests
                if (
                    skipDuplicate ||
                    (equipmentId &&
                        (!listOfRequests || listOfRequests.length === 0))
                ) {
                    console.log(
                        'Skipping duplicates step because:',
                        skipDuplicate
                            ? 'skipDuplicate flag is set'
                            : 'no linked requests',
                    )
                    setSkipDuplicate(false)
                    handleNextStep({
                        steps,
                        setSteps,
                        setError,
                        setSkipDuplicate,
                        selectedAsset,
                        handleGetAssetInfo,
                        handleChangeAlert,
                        formRef,
                        history,
                        handleChangeOpen,
                        currentStep: activeStep?.name,
                        newStep: 'Request Details',
                    })
                } else {
                    console.log(
                        'Not skipping duplicates - requests found:',
                        listOfRequests?.length,
                    )
                }
            } else {
                console.log('Asset ID mismatch - waiting for fresh data')
            }
        }
    }, [
        steps,
        skipDuplicate,
        listOfRequests,
        equipmentId,
        isLoadingAsset,
        selectedAsset,
    ])

    useEffect(() => {
        if (
            equipmentId &&
            equipmentFetchInitializedRef.current !== equipmentId
        ) {
            equipmentFetchInitializedRef.current = equipmentId
            handleGetAssetInfo(equipmentId)
        }
    }, [equipmentId])

    useEffect(() => {
        if (selectedAsset?.length === 1) {
            setError(null)
        }
    }, [selectedAsset])

    useEffect(() => {
        if (error) {
            setAfterHoursModalOpen(false)
        }
    }, [error])

    const onGettedAssetError = error => {
        tagHotjarRecording('API Error')
        const message =
            error?.response?.data?.message || 'Unable to load equipment.'
        setError(message)
        console.log(message)
        changeLoading(false)
    }

    const onGettedAssetSuccess = response => {
        const asset = response?.data?.asset
        const assetRequests = asset?.openRequests || []

        const handlers = {
            setListOfRequests,
            setSkipDuplicate,
            setAssetInfo,
            changeLoading,
        }

        handleAssetUpdate(asset, assetRequests, handlers, customAssetID)

        if (equipmentId && (!assetRequests || assetRequests.length === 0)) {
            console.log(
                'No linked requests found for asset with equipmentId, skipping duplicates step',
            )
            setSkipDuplicate(true)
        }

        equipmentId && setSelectedAsset([asset])
    }

    const handleGetAssetInfo = async customId => {
        setIsLoadingAsset(true)
        changeLoading(true)
        const selectedAssetId = customId || selectedAsset[0]?.salesforceID
        const paramsToGetFleetAsset = {
            assetId: selectedAssetId,
            companyId: user?.company?.id,
            getRequests: true,
        }

        if (isOffline) {
            const handlers = {
                setListOfRequests,
                setSkipDuplicate,
                setAssetInfo,
                changeLoading,
            }

            try {
                const asset = await getFleetById(selectedAssetId)
                const storedOpenRequests = await getRequestsByType(
                    'openRequest',
                )
                const formatStoredOpenRequests = formatRequestsListTableCheckDuplicates(
                    storedOpenRequests,
                    selectedAsset[0],
                )

                // Update the last processed asset ID
                lastProcessedAssetIdRef.current =
                    asset?.salesforceID || asset?.customId

                checkDuplicates(asset, formatStoredOpenRequests, handlers)

                // When coming from an equipmentId in the URL, explicitly check for requests
                if (equipmentId) {
                    // Get requests specifically linked to this asset
                    const linkedRequests = formatStoredOpenRequests.filter(
                        request => request.equipmentId === asset.salesforceID,
                    )

                    if (!linkedRequests || linkedRequests.length === 0) {
                        console.log(
                            'No linked requests found for asset with equipmentId in offline mode',
                        )
                        setSkipDuplicate(true)
                    }
                }

                equipmentId && setSelectedAsset([asset])
                setIsLoadingAsset(false)
            } catch (error) {
                console.error('Error in offline asset loading:', error)
                setIsLoadingAsset(false)
                changeLoading(false)
            }
        } else {
            handleGetAsset(
                paramsToGetFleetAsset,
                response => {
                    // Update the last processed asset ID
                    const asset = response?.data?.asset
                    lastProcessedAssetIdRef.current =
                        asset?.salesforceID || asset?.customId

                    onGettedAssetSuccess(response)
                    setIsLoadingAsset(false)
                },
                error => {
                    onGettedAssetError(error)
                    setIsLoadingAsset(false)
                },
            )
        }
        tagHotjarRecording('Repair Request')
    }

    const onNextStep = () => {
        handleNextStep({
            steps,
            setSteps,
            setError,
            setSkipDuplicate,
            selectedAsset,
            handleGetAssetInfo,
            handleChangeAlert,
            formRef,
            history,
            handleChangeOpen,
        })
    }

    const onCheckAftersHours = formValues => {
        if (error) {
            setFormValues(formValues)
            onCreateRequest(lastAfterHoursApproval, formValues)
            return
        }

        checkAfterHours({
            asset: selectedAsset[0],
            setFormValues,
            formValues,
            setAfterHoursModalOpen,
            onCreateRequest,
        })
    }

    const { handleRequest } = createRequestHandler({
        setSuccessfulSubmit,
        setError,
        changeLoading,
        setAfterHoursModalOpen,
        changeNeedsUpdate,
        sendEventToIntercom,
        handleChangeAlert,
        handleChangeOpen,
        history,
        processImagesHelper,
        handleCreateServiceRequest,
        setImageRetryAttempted,
    })

    const onCreateRequest = useCallback(
        (afterHoursApproval, data) => {
            setLastAfterHoursApproval(afterHoursApproval)
            handleRequest({
                afterHoursApproval,
                formValues,
                assetInfo,
                images,
                photosIds,
                user,
                isOffline,
                isEstimate,
                imageRetryAttempted,
                data,
            })
        },
        [
            formValues,
            assetInfo,
            images,
            photosIds,
            user,
            isOffline,
            isEstimate,
            imageRetryAttempted,
            lastAfterHoursApproval,
            error,
        ],
    )

    return (
        <RequestPageComponent
            isEstimate={isEstimate}
            steps={steps}
            setSteps={setSteps}
            afterHoursModalOpen={afterHoursModalOpen}
            setAfterHoursModalOpen={setAfterHoursModalOpen}
            onCreateRequest={onCreateRequest}
            selectedAsset={selectedAsset}
            setSelectedAsset={setSelectedAsset}
            filteredOpenRequests={filteredOpenRequests}
            onFilterOpenRequests={onFilterOpenRequests}
            onCheckAftersHours={onCheckAftersHours}
            formRef={formRef}
            error={error}
            setError={setError}
            isValidForm={isValidForm}
            allowSubmitWithError={imageRetryAttempted}
            setIsValidForm={setIsValidForm}
            successfulSubmit={successfulSubmit}
            setSuccessfulSubmit={setSuccessfulSubmit}
            resetTouch={resetTouch}
            setResetTouch={setResetTouch}
            images={images}
            photosIds={photosIds}
            setImages={setImages}
            setPhotosIds={setPhotosIds}
            onNextStep={onNextStep}
            history={history}
            listOfRequests={listOfRequests}
        />
    )
}

CreateRepairRequestFlow.propTypes = {
    history: PropTypes.shape({
        push: PropTypes.func,
        goBack: PropTypes.func,
    }),
}

export default CreateRepairRequestFlow
